import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import useCenterlineAiStyles from './styles';
import { Chat } from './core/chat-reducer';
import { chatRowTitle } from './core/titles';
import { useCenterlineAi } from './CenterlineAiProvider';
import {
  ChevronLeftGlyph,
  CloseGlyph,
  DeleteGlyph,
  MoreGlyph,
  PlusGlyph,
  RenameGlyph,
} from './Glyphs';

/**
 * The chats sidebar (chat-ui-spec §3): a navy header with the rail toggle, New chat, this
 * project's chats with a row menu (Rename, Delete) and inline rename, and the footer note.
 * With `onClose` it is the narrow layout's chats panel (ChatsAndConversation): no rail, a Close
 * button in its place, and opening or starting a chat closes it.
 */

interface ChatRowProps {
  chat: Chat;
  active: boolean;
  onOpened?: () => void;
}

function ChatRow({ chat, active, onOpened }: ChatRowProps) {
  const classes = useCenterlineAiStyles();
  const { actions } = useCenterlineAi();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const cancelled = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const title = chatRowTitle(chat.title);

  useEffect(() => {
    if (renaming) {
      input.current?.focus();
      input.current?.select();
    }
  }, [renaming]);

  const startRename = () => {
    setMenuAnchor(null);
    cancelled.current = false;
    setDraft(chat.title ?? '');
    setRenaming(true);
  };

  // Enter or leaving the box commits; Escape cancels (§3.6).
  const finishRename = () => {
    if (!cancelled.current) actions.renameChat(chat.localId, draft);
    setRenaming(false);
  };

  return (
    <li className={clsx(classes.chatRow, active && classes.chatRowActive)}>
      {renaming ? (
        <input
          ref={input}
          className={classes.renameInput}
          aria-label="Chat name"
          value={draft}
          maxLength={120}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={finishRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              input.current?.blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelled.current = true;
              input.current?.blur();
            }
          }}
        />
      ) : (
        <>
          <button
            type="button"
            className={classes.rowOpen}
            title={title}
            aria-current={active ? 'page' : undefined}
            onClick={() => {
              actions.openChat(chat.localId);
              onOpened?.();
            }}
          >
            {title}
          </button>
          <button
            type="button"
            className={classes.rowMore}
            aria-label={`Actions for ${title}`}
            aria-haspopup="true"
            aria-expanded={!!menuAnchor}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <MoreGlyph />
          </button>
          {/* MUI's menu brings arrow keys, Escape and focus return (§10 gaps). */}
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            getContentAnchorEl={null}
            classes={{ paper: classes.rowMenuPaper }}
          >
            <MenuItem className={classes.menuItem} onClick={startRename}>
              <RenameGlyph />
              Rename
            </MenuItem>
            <MenuItem
              className={clsx(classes.menuItem, classes.menuDanger)}
              onClick={() => {
                setMenuAnchor(null);
                actions.deleteChat(chat.localId);
              }}
            >
              <DeleteGlyph />
              Delete
            </MenuItem>
          </Menu>
        </>
      )}
    </li>
  );
}

export default function ChatSidebar({ onClose }: { onClose?: () => void }) {
  const classes = useCenterlineAiStyles();
  const { state, actions } = useCenterlineAi();
  const panel = !!onClose;
  const rail = !panel && state.sidebarRail;
  const toggleLabel = rail ? 'Expand sidebar' : 'Collapse sidebar';
  const newChat = () => {
    actions.newChat();
    onClose?.();
  };

  return (
    <aside
      className={clsx(
        classes.card,
        classes.sidebar,
        rail && classes.sidebarRail,
        panel && classes.sidebarPanel,
      )}
      aria-label="Chats"
    >
      <div className={clsx(classes.sidebarHead, rail && classes.sidebarHeadRail)}>
        {rail ? null : <h2 className={classes.sidebarTitle}>Chats</h2>}
        {panel ? (
          <button
            type="button"
            className={classes.headButton}
            aria-label="Close chats"
            title="Close chats"
            onClick={onClose}
          >
            <CloseGlyph />
          </button>
        ) : (
          <button
            type="button"
            className={clsx(classes.headButton, rail && classes.headButtonRail)}
            aria-label={toggleLabel}
            title={toggleLabel}
            onClick={actions.toggleRail}
          >
            <ChevronLeftGlyph />
          </button>
        )}
      </div>
      {rail ? (
        <button
          type="button"
          className={classes.newChatRail}
          aria-label="New chat"
          title="New chat"
          onClick={newChat}
        >
          <PlusGlyph />
        </button>
      ) : (
        <>
          <div className={classes.newChatWrap}>
            <button type="button" className={classes.newChat} title="New chat" onClick={newChat}>
              <PlusGlyph />
              New chat
            </button>
          </div>
          <ul className={classes.chatList}>
            {state.chats.length === 0 ? (
              <li className={classes.chatsEmpty}>No previous chats.</li>
            ) : (
              state.chats.map((chat) => (
                <ChatRow
                  key={chat.localId}
                  chat={chat}
                  active={chat.localId === state.activeId}
                  onOpened={onClose}
                />
              ))
            )}
          </ul>
          <p className={classes.sidebarFoot}>Your chats in this project. Deleting one hides it.</p>
        </>
      )}
    </aside>
  );
}
